import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiRequestError } from '@/lib/api';
import { AuthLayout, FormError } from './auth-layout';
import { useAcceptInvite, useForgotPassword, useResetPassword } from './use-session';

/** Mirrors the server's policy so the two never disagree. */
const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .refine((v) => /[a-z]/.test(v), 'Include a lowercase letter')
  .refine((v) => /[A-Z]/.test(v), 'Include an uppercase letter')
  .refine((v) => /[0-9]/.test(v), 'Include a number');

// ── Forgot password ──────────────────────────────────────────────────────────

const forgotSchema = z.object({ email: z.string().email('Enter a valid email') });

export function ForgotPasswordPage() {
  const forgot = useForgotPassword();
  const [sent, setSent] = useState(false);
  const form = useForm<z.infer<typeof forgotSchema>>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  });

  if (sent) {
    return (
      <AuthLayout title="Check your email">
        <div className="space-y-4 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <p className="text-sm text-muted-foreground">
            If that email is registered, a reset link is on its way. It expires in one hour.
          </p>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">Back to sign in</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      description="We will email you a link to choose a new one."
      footer={
        <Link to="/login" className="hover:text-foreground hover:underline">
          Back to sign in
        </Link>
      }
    >
      <form
        onSubmit={form.handleSubmit(async (values) => {
          await forgot.mutateAsync(values).catch(() => undefined);
          // Always the same outcome, so this cannot reveal who has an account.
          setSent(true);
        })}
        className="space-y-4"
      >
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoFocus autoComplete="email" {...form.register('email')} />
          {form.formState.errors.email && (
            <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
          )}
        </div>
        <Button type="submit" className="w-full" disabled={forgot.isPending}>
          {forgot.isPending ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
    </AuthLayout>
  );
}

// ── Reset password ───────────────────────────────────────────────────────────

const resetSchema = z
  .object({ password: passwordSchema, confirm: z.string() })
  .refine((d) => d.password === d.confirm, {
    message: 'Both passwords must match',
    path: ['confirm'],
  });

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const reset = useResetPassword();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<z.infer<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirm: '' },
  });

  if (!token) {
    return (
      <AuthLayout title="Link not valid">
        <p className="text-sm text-muted-foreground">
          This reset link is missing its token. Request a new one.
        </p>
        <Button asChild className="mt-4 w-full">
          <Link to="/forgot-password">Request a new link</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      <form
        onSubmit={form.handleSubmit(async (values) => {
          setError(null);
          try {
            await reset.mutateAsync({ token, password: values.password });
            navigate('/login', { replace: true });
          } catch (caught) {
            setError(
              caught instanceof ApiRequestError ? caught.message : 'Could not reset your password.',
            );
          }
        })}
        className="space-y-4"
      >
        <PasswordFields form={form} />
        <FormError message={error} />
        <Button type="submit" className="w-full" disabled={reset.isPending}>
          {reset.isPending ? 'Saving…' : 'Set new password'}
        </Button>
      </form>
    </AuthLayout>
  );
}

// ── Accept invitation ────────────────────────────────────────────────────────

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const accept = useAcceptInvite();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<z.infer<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirm: '' },
  });

  if (!token) {
    return (
      <AuthLayout title="Invitation not valid">
        <p className="text-sm text-muted-foreground">
          This invitation link is missing its token. Ask your administrator to send a new one.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome aboard"
      description="Set a password to finish setting up your account."
    >
      <form
        onSubmit={form.handleSubmit(async (values) => {
          setError(null);
          try {
            await accept.mutateAsync({ token, password: values.password });
            navigate('/dashboard', { replace: true });
          } catch (caught) {
            setError(
              caught instanceof ApiRequestError
                ? caught.message
                : 'Could not accept this invitation.',
            );
          }
        })}
        className="space-y-4"
      >
        <PasswordFields form={form} />
        <FormError message={error} />
        <Button type="submit" className="w-full" disabled={accept.isPending}>
          {accept.isPending ? 'Setting up…' : 'Create my account'}
        </Button>
      </form>
    </AuthLayout>
  );
}

// ── Shared fields ────────────────────────────────────────────────────────────

type PasswordForm = ReturnType<typeof useForm<z.infer<typeof resetSchema>>>;

function PasswordFields({ form }: { form: PasswordForm }) {
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="password">New password</Label>
        <Input
          id="password"
          type="password"
          autoFocus
          autoComplete="new-password"
          aria-invalid={Boolean(form.formState.errors.password)}
          {...form.register('password')}
        />
        {form.formState.errors.password ? (
          <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
        ) : (
          <p className="text-xs text-muted-foreground">
            At least 10 characters, with an uppercase letter, a lowercase letter and a number.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="confirm">Confirm password</Label>
        <Input
          id="confirm"
          type="password"
          autoComplete="new-password"
          aria-invalid={Boolean(form.formState.errors.confirm)}
          {...form.register('confirm')}
        />
        {form.formState.errors.confirm && (
          <p className="text-xs text-destructive">{form.formState.errors.confirm.message}</p>
        )}
      </div>
    </>
  );
}
